import {
  AlignmentType,
  BorderStyle,
  IParagraphOptions,
  IRunOptions,
  ITableCellOptions,
  Paragraph,
  ParagraphChild,
  Table,
  TableCell,
  TableRow,
  TextRun,
  UnderlineType,
  WidthType,
} from "docx";

const DEFAULT_FONTSIZE = 26;
const DEFAULT_SPACING_LINE = 276;

const _textRun = (params: IRunOptions) => {
  return new TextRun({
    size: DEFAULT_FONTSIZE,
    font: "Times New Roman",
    ...params,
  });
};

/**
 * @example text("Hello Word!")
 * @example text({ text: "Hello World!", bold: true })
 * @example text([{ text: "Hello ", bold: true }, { text: "World!", italics: true }])
 * @example text([{ text: "Hello ", bold: true }, { text: "World!", italics: true }], { alignment: AlignmentType.CENTER })
 */
export const text = (
  textRunParams: IRunOptions[] | IRunOptions | string,
  paragraphParams?: IParagraphOptions
) => {
  let children: ParagraphChild[] | undefined;

  if (Array.isArray(textRunParams)) {
    children = textRunParams.map(_textRun);
  } else if (typeof textRunParams === "string") {
    children = [_textRun({ text: textRunParams })];
  } else {
    children = [_textRun(textRunParams)];
  }

  return new Paragraph({
    children,

    spacing: {
      line: DEFAULT_SPACING_LINE,
    },

    alignment: AlignmentType.JUSTIFIED,
    ...paragraphParams,
  });
};

export const line = (indent: number) => {
  return text(" ", {
    border: {
      top: {
        color: "000000", size: 3, style: BorderStyle.SINGLE
      }
    },
    indent: {left: indent, right: indent}
  })
}

const tableCellMargins = {
  top: 100,
  bottom: 100,
  left: 200,
  right: 200,
};

export type DocXTableColumn<T> = {
  label: string | IRunOptions;
  renderText: (
    item: T,
    index: number
  ) => (Paragraph | Table)[] | (Paragraph | Table);
};

type DocXTableParams<T> = {
  columns: DocXTableColumn<T>[];
  data: T[];
};

/**
 * @example
 * table<{ name: string }>({
 *  columns: [
 *    {
 *      label: "Name",
 *      renderText: (item, index) => text(item.name)
 *    }
 *  ],
 *  data: [{ id: 1, name: "John" }]
 * })
 */
export const table = <T>({ columns, data }: DocXTableParams<T>) => {
  return new Table({
    width: {
      size: "100%",
      type: WidthType.PERCENTAGE,
    },
    rows: [
      new TableRow({
        children: columns.map((col) => {
          return new TableCell({
            margins: tableCellMargins,
            children: [
              text(
                typeof col.label === "string"
                  ? {
                      text: col.label,
                      bold: true,
                    }
                  : { bold: true, ...col.label },
                {
                  alignment: AlignmentType.CENTER,
                }
              ),
            ],
          });
        }),
      }),
      ...data.map((row: any, index: number) => {
        return new TableRow({
          children: [
            ...columns.map((cell) => {
              const value = cell.renderText(row, index);

              return new TableCell({
                margins: tableCellMargins,
                children: Array.isArray(value) ? value : [value],
              });
            }),
          ],
        });
      }),
    ],
  });
};

const leftParagraph = [
  text(
    { text: "TCT HÀNG KHÔNG VIỆT NAM", size: 22 },
    { alignment: AlignmentType.CENTER }
  ),
  text(
    { text: "CÔNG TY DVMĐ SÂN BAY VIỆT NAM", size: 24, bold: true },
    { alignment: AlignmentType.CENTER }
  ),
  line(800)
];

const rightParagraph = [
  text(
    [
      {
        text: "CỘNG HOÀ XÃ HỘI CHỦ NGHĨA VIỆT NAM",
        bold: true,
        size: 22,
      },
    ],
    {
      alignment: AlignmentType.CENTER,
    }
  ),
  text(
    [
      {
        text: "Độc lập – Tự do – Hạnh phúc",
        size: 24,
        bold: true,
        underline: {
          type: UnderlineType.SINGLE,
        },
      },
    ],
    {
      alignment: AlignmentType.CENTER,
    }
  ),
];

/**
 * * DocX Templates
 */
export const Header = {
  rightParagraph,
  leftParagraph,
};

export const Body = {
  Date: text(
    [
      {
        text: "Tp. Hồ Chí Minh, ngày    tháng    năm       .",
        italics: true,
      },
    ],
    {
      alignment: AlignmentType.RIGHT,
    }
  ),

  save: (...places: string[]) => {
    return text(
      [
        {
          text: "Nơi nhận",
          italics: true,
          bold: true,
        },
        ...places.map((p) => ({
          text: `- ${p}`,
          size: DEFAULT_FONTSIZE * 0.85,
          break: 1,
        })),
      ],
      {
        alignment: AlignmentType.LEFT,
      }
    );
  },
  sign: text(
    [
      { text: "T/L TỔNG GIÁM ĐỐC", bold: true },
      { text: "CHÁNH VĂN PHÒNG CÔNG TY", bold: true, break: 1 },
      { text: "Trần Đức Hiệp", bold: true, break: 4 },
    ],
    {
      alignment: AlignmentType.CENTER,
    }
  ),
  sign2: text(
    [
      { text: "CHÁNH VĂN PHÒNG CÔNG TY", bold: true },
      { text: "Trần Đức Hiệp", bold: true, break: 5 },
    ],
    { alignment: AlignmentType.CENTER }
  ),
  sign3: text(
    [
      { text: " KT. TỔNG GIÁM ĐỐC", bold: true, break: 1 },
      { text: " PHÓ TỔNG GIÁM ĐỐC", bold: true, break: 1 },
    ],
    { alignment: AlignmentType.CENTER }
  ),
  
};

export const breakLine = (line: number = 1) => {
  return text(
    { break: 1 },
    { spacing: { line: (line * DEFAULT_SPACING_LINE) / 2.5 } }
  );
};

export const C = {
  dot: (line: number = 1) =>
    Array.from({ length: line }).map(() => {
      return new Paragraph({
        text: "........................................................................................................................................................",
        alignment: AlignmentType.LEFT,
      });
    }),
  row: (...cells: ITableCellOptions[]) => {
    return new Table({
      width: {
        size: "100%",
        type: WidthType.PERCENTAGE,
      },
      rows: [
        new TableRow({
          children: cells.map((cell) => {
            return new TableCell({
              borders: {
                top: {
                  style: BorderStyle.NIL,
                },
                bottom: {
                  style: BorderStyle.NIL,
                },
                left: {
                  style: BorderStyle.NIL,
                },
                right: {
                  style: BorderStyle.NIL,
                },
              },
              ...cell,
            });
          }),
        }),
      ],
    });
  },
  col: (
    percent: number | string | undefined | Omit<ITableCellOptions, "children">,
    children: (Paragraph | Table)[] = []
  ): ITableCellOptions => {
    return {
      ...(percent &&
      (typeof percent === "string" || typeof percent === "number")
        ? {
            width: {
              size: typeof percent === "number" ? percent + "%" : `${percent}%`,
              type: WidthType.PERCENTAGE,
            },
          }
        : { ...(percent as ITableCellOptions), children: [] }),
      children,
    };
  },
};
